import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bell, Calendar, Code, Copy, Check, Video, Building, Palette, Star, MessageSquare } from "lucide-react";
import FileUpload from "@/components/FileUpload";

function useCopy() {
  const [copied, setCopied] = useState(false);
  return { copied, copy: (text) => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } };
}

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function DoctorIntegrations({ doctorId, doctor, onRefresh }) {
  const [feedKey, setFeedKey] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [color, setColor] = useState(doctor?.widget_color || "#2563eb");
  const [logo, setLogo] = useState(doctor?.widget_logo || "");
  const [reminderSubject, setReminderSubject] = useState("");
  const [reminderDefault, setReminderDefault] = useState("");
  const [reminderPreview, setReminderPreview] = useState("");
  const c1 = useCopy(); const c2 = useCopy(); const c3 = useCopy(); const c4 = useCopy();

  useEffect(() => {
    api.get("/doctor/calendar-feed").then(r => setFeedKey(r.data.key));
    api.get("/doctor/review-reminder-subject").then(r => {
      setReminderSubject(r.data.subject || "");
      setReminderDefault(r.data.default || "");
      setReminderPreview(r.data.preview || "");
    });
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription().then(s => setPushOn(!!s)));
    }
  }, []);

  const saveReminderSubject = async () => {
    await api.put("/doctor/review-reminder-subject", { subject: reminderSubject });
    const r = await api.get("/doctor/review-reminder-subject");
    setReminderPreview(r.data.preview || "");
    toast.success("Asunto guardado");
  };

  const enableFeed = async () => {
    const r = await api.post("/doctor/calendar-feed/enable");
    setFeedKey(r.data.key); toast.success("Feed activado");
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const feedUrl = feedKey ? `${backendUrl}/api/doctors/${doctorId}/calendar.ics?key=${feedKey}` : null;
  const gcalSubscribeUrl = feedUrl ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl.replace("https://","webcal://"))}` : null;
  const embedSrc = `${window.location.origin}/embed/${doctorId}`;
  const embedSnippet = `<iframe src="${embedSrc}" width="380" height="620" style="border:1px solid #e2e8f0;border-radius:16px;box-shadow:0 4px 12px rgba(0,0,0,.05)"></iframe>`;
  const shareUrl = `${window.location.origin}/doctor/${doctorId}`;
  const badgeUrl = `${backendUrl}/api/doctors/${doctorId}/reviews-badge.svg`;
  const reviewsWidgetUrl = `${backendUrl}/api/doctors/${doctorId}/reviews-widget.html`;
  const badgeSnippet = `<a href="${shareUrl}" target="_blank" rel="noopener"><img src="${badgeUrl}" alt="Reseñas Agendik" width="300" height="60" style="border:0"/></a>`;
  const reviewsIframeSnippet = `<iframe src="${reviewsWidgetUrl}" width="320" height="70" style="border:0;overflow:hidden" title="Reseñas Agendik"></iframe>`;

  const subscribePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return toast.error("Tu navegador no soporta Web Push");
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return toast.error("Permiso denegado");
    const reg = await navigator.serviceWorker.ready;
    const keyRes = await api.get("/push/public-key");
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(keyRes.data.key),
    });
    const j = sub.toJSON();
    await api.post("/doctor/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
    setPushOn(true);
    toast.success("Notificaciones push activadas");
  };

  const unsubscribePush = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const j = sub.toJSON();
      await api.post("/doctor/push/unsubscribe", { endpoint: j.endpoint, keys: j.keys });
      await sub.unsubscribe();
    }
    setPushOn(false); toast.success("Notificaciones desactivadas");
  };

  const saveWidget = async () => {
    await api.put("/doctor/widget", { widget_color: color, widget_logo: logo });
    toast.success("Widget actualizado"); onRefresh?.();
  };

  return (
    <div className="space-y-5">
      {/* Consultation type */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Building size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Modo de consulta</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label data-testid="mode-in-person" className="flex items-start gap-3 p-3 rounded-xl border-2 border-blue-500 bg-blue-50 cursor-pointer">
            <input type="radio" checked readOnly className="mt-1 accent-blue-600"/>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Presencial</div>
              <div className="text-xs text-slate-500">Activo por defecto</div>
            </div>
          </label>
          <label data-testid="mode-online" className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed">
            <input type="radio" disabled className="mt-1"/>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Video size={14} className="text-slate-400"/>
                <div className="font-semibold text-slate-500 text-sm">En línea / A distancia</div>
              </div>
              <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Próximamente</span>
            </div>
          </label>
        </div>
      </CardContent></Card>

      {/* Google Calendar */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Google Calendar</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">Sincroniza tus citas confirmadas con Google Calendar / Apple / Outlook.</p>
        {!feedKey ? (
          <Button data-testid="enable-feed-btn" onClick={enableFeed} className="bg-blue-600 hover:bg-blue-700">Activar mi feed</Button>
        ) : (
          <>
            <Label className="text-xs">URL de suscripción</Label>
            <div className="flex gap-2">
              <Input readOnly value={feedUrl} data-testid="feed-url" className="text-xs font-mono"/>
              <Button data-testid="copy-feed-btn" size="icon" variant="outline" onClick={() => c1.copy(feedUrl)}>
                {c1.copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
              </Button>
            </div>
            <a href={gcalSubscribeUrl} target="_blank" rel="noreferrer"
               className="mt-3 inline-flex items-center gap-2 text-sm text-blue-700 hover:underline">
              Añadir directo a Google Calendar →
            </a>
          </>
        )}
      </CardContent></Card>

      {/* Web Push VAPID */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Notificaciones al teléfono (Web Push)</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Notificaciones nativas — te llegan aunque cierres el navegador. En iOS, instala primero la app en pantalla de inicio (iOS 16.4+).
        </p>
        {pushOn ? (
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              ✓ Notificaciones push activas
            </div>
            <Button data-testid="unsub-push-btn" size="sm" variant="outline" onClick={unsubscribePush}>Desactivar</Button>
          </div>
        ) : (
          <Button data-testid="sub-push-btn" onClick={subscribePush} className="bg-blue-600 hover:bg-blue-700">
            Activar notificaciones push
          </Button>
        )}
      </CardContent></Card>

      {/* Widget customization */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Palette size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Personalización del widget</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Opcional · Personaliza el widget con tu logo y color principal para que combine con tu identidad visual.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Color principal</Label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="widget-color-input" type="color" value={color}
                     onChange={e => setColor(e.target.value)}
                     className="w-14 h-10 rounded border border-slate-200 cursor-pointer"/>
              <Input value={color} onChange={e => setColor(e.target.value)} className="text-xs font-mono"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Logo (opcional)</Label>
            <FileUpload testid="widget-logo" label="Subir logo (opcional)"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp" maxMB={2}
                        preview={logo}
                        onData={(d) => setLogo(d)}/>
          </div>
        </div>
        <Button data-testid="save-widget-btn" onClick={saveWidget} className="mt-4 bg-blue-600 hover:bg-blue-700">
          Guardar personalización
        </Button>
      </CardContent></Card>

      {/* Review reminder subject */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Asunto del email de reseña</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Personaliza el asunto del email de recordatorio de reseña. Puedes usar <code className="px-1 bg-slate-100 rounded">{'{doctor_name}'}</code> y <code className="px-1 bg-slate-100 rounded">{'{clinic_name}'}</code>.
        </p>
        <Input data-testid="review-subject-input" value={reminderSubject}
               placeholder={reminderDefault || "Califica tu cita con Dr. {doctor_name} - {clinic_name}"}
               onChange={e => setReminderSubject(e.target.value)}
               maxLength={150} className="text-sm"/>
        <div className="mt-2 text-[11px] text-slate-500">
          Vista previa: <span data-testid="review-subject-preview" className="font-medium text-slate-800">{reminderPreview}</span>
        </div>
        <Button data-testid="save-review-subject-btn" size="sm" onClick={saveReminderSubject}
                className="mt-3 bg-blue-600 hover:bg-blue-700">
          Guardar asunto
        </Button>
      </CardContent></Card>

      {/* Reviews stars widget */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Star size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Widget de reseñas para tu web / Instagram</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Copia una insignia con tu promedio de estrellas y el número de reseñas. Se actualiza automáticamente.
        </p>
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 mb-3">
          <img data-testid="reviews-badge-preview" src={badgeUrl} alt="Preview" width="300" height="60"/>
        </div>
        <Label className="text-xs">HTML (badge SVG - para tu sitio)</Label>
        <div className="flex gap-2">
          <Input readOnly value={badgeSnippet} data-testid="reviews-badge-snippet" className="text-xs font-mono"/>
          <Button data-testid="copy-reviews-badge-btn" size="icon" variant="outline" onClick={() => c3.copy(badgeSnippet)}>
            {c3.copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
          </Button>
        </div>
        <Label className="text-xs mt-3 block">Iframe interactivo</Label>
        <div className="flex gap-2">
          <Input readOnly value={reviewsIframeSnippet} data-testid="reviews-iframe-snippet" className="text-xs font-mono"/>
          <Button data-testid="copy-reviews-iframe-btn" size="icon" variant="outline" onClick={() => c4.copy(reviewsIframeSnippet)}>
            {c4.copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Para Instagram: copia la imagen (clic derecho → Guardar imagen) o comparte el <a href={shareUrl} className="text-blue-600 underline">enlace a tu perfil</a>.
        </p>
      </CardContent></Card>

      {/* Widget snippet */}
      <Card><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Code size={18} className="text-blue-600"/>
          <h3 className="font-semibold text-slate-900">Widget para mi sitio</h3>
        </div>
        <Label className="text-xs">Enlace directo (Instagram)</Label>
        <div className="flex gap-2">
          <Input readOnly value={shareUrl} data-testid="share-url" className="text-xs font-mono"/>
          <Button data-testid="copy-share-btn" size="icon" variant="outline" onClick={() => c2.copy(shareUrl)}>
            {c2.copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
          </Button>
        </div>
        <Label className="text-xs mt-3 block">Iframe para tu sitio web</Label>
        <textarea data-testid="embed-snippet" readOnly value={embedSnippet}
                  className="w-full h-24 rounded-md border border-slate-200 p-2 text-xs font-mono resize-none bg-slate-50"/>
        <Button data-testid="copy-embed-btn" size="sm" variant="outline"
                onClick={() => { navigator.clipboard.writeText(embedSnippet); toast.success("Copiado"); }}
                className="mt-2">Copiar snippet</Button>
        <p className="text-[11px] text-slate-500 mt-3">Vista previa:</p>
        <iframe src={embedSrc} width="380" height="500" title="preview"
                className="mt-1 border border-slate-200 rounded-xl w-full max-w-md"/>
      </CardContent></Card>
    </div>
  );
}
